/**
 * `/demo` access gate (#2985).
 *
 * The demo pages render at once: a visitor can read what the demo is before
 * any key. The gate sits on the start action instead. A start button calls
 * `ensureDemoAccess()` (from `useDemoAccess`) before it creates a demo team:
 *
 *   - open: resolves `true` at once.
 *   - key: resolves `true` when this tab holds a live proof. Otherwise it
 *     opens the key prompt and resolves `true` once
 *     `POST /api/demo-sessions/access/key` returns a proof, `false` if the
 *     visitor closes the prompt.
 *   - closed: opens a short "closed" notice and resolves `false`.
 *
 * So no demo team request leaves the page before the key passes. The server
 * checks the mode and the proof on every demo route anyway
 * (`backend/src/api/demo_access_gate.py`): this is the face of the gate, not
 * the gate.
 *
 * An older API with no gate route (404/405) resolves `true`, the same posture
 * as the mint fallback in `DemoStartPage`. Outside the provider the hook also
 * resolves `true`: the server still refuses an ungated request.
 */
import {
  type FormEvent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";

import { ApiError, apiFetch } from "../api/client";
import { Button, Input, Label } from "../components/ui";
import { ResponsiveModal } from "../components/ui/ResponsiveModal";
import {
  type DemoAccessMode,
  type DemoAccessProof,
  readDemoAccessProof,
  storeDemoAccessProof,
} from "../lib/demoAccess";
import { CONTACT_EMAIL } from "./marketing/MarketingChrome";

interface DemoAccessContextValue {
  ensureDemoAccess: () => Promise<boolean>;
}

const DemoAccessContext = createContext<DemoAccessContextValue>({
  ensureDemoAccess: async () => true,
});

export function useDemoAccess(): DemoAccessContextValue {
  return useContext(DemoAccessContext);
}

type PromptState = "key" | "closed" | "error" | null;

export function DemoAccessGate({ children }: { children: ReactNode }) {
  const [prompt, setPrompt] = useState<PromptState>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setPrompt(null);
    resolve?.(ok);
  }, []);

  const ensureDemoAccess = useCallback(async (): Promise<boolean> => {
    let mode: DemoAccessMode;
    try {
      mode = (await apiFetch<{ mode: DemoAccessMode }>("/api/demo-sessions/access")).mode;
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 405)) return true;
      return openPrompt("error");
    }
    if (mode === "open") return true;
    if (mode === "key" && readDemoAccessProof() !== null) return true;
    return openPrompt(mode);

    function openPrompt(next: Exclude<PromptState, null>): Promise<boolean> {
      resolverRef.current?.(false);
      return new Promise<boolean>((resolve) => {
        resolverRef.current = resolve;
        setPrompt(next);
      });
    }
  }, []);

  const value = useMemo(() => ({ ensureDemoAccess }), [ensureDemoAccess]);

  return (
    <DemoAccessContext.Provider value={value}>
      {children}
      <ResponsiveModal
        open={prompt !== null}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
        title={
          prompt === "closed"
            ? "The demo is closed"
            : prompt === "error"
              ? "We could not reach the demo"
              : "Enter your access key"
        }
        testid="demo-access-prompt"
      >
        {prompt === "key" ? <DemoKeyForm onPassed={() => settle(true)} /> : null}
        {prompt === "closed" ? (
          <p className="text-sm leading-relaxed text-fg-muted" data-testid="demo-access-closed">
            The demo is not open right now. Please check back later.
          </p>
        ) : null}
        {prompt === "error" ? (
          <p className="text-sm leading-relaxed text-fg-muted" data-testid="demo-access-error">
            Try again in a moment.
          </p>
        ) : null}
      </ResponsiveModal>
    </DemoAccessContext.Provider>
  );
}

function DemoKeyForm({ onPassed }: { onPassed: () => void }) {
  const [key, setKey] = useState("");
  const [sending, setSending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!key.trim()) return;
    setSending(true);
    setErrorMessage(null);
    try {
      const proof = await apiFetch<DemoAccessProof>("/api/demo-sessions/access/key", {
        method: "POST",
        body: JSON.stringify({ key }),
      });
      storeDemoAccessProof(proof);
      onPassed();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setErrorMessage("That access key is not right.");
      } else if (err instanceof ApiError && err.status === 403) {
        setErrorMessage("The demo is closed right now.");
      } else if (err instanceof ApiError) {
        setErrorMessage(err.detail);
      } else {
        setErrorMessage("Something went wrong. Try again in a moment.");
      }
      setSending(false);
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => void submit(e)}
      data-testid="demo-access-form"
    >
      <p className="text-sm leading-relaxed text-fg-muted">
        The demo opens with an access key. If you do not have one,{" "}
        <a
          href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Demo access key")}`}
          className="underline underline-offset-2 hover:text-fg"
          data-testid="demo-access-contact"
        >
          contact us
        </a>{" "}
        for one.
      </p>
      <Label htmlFor="demo-access-key">Access key</Label>
      <Input
        id="demo-access-key"
        name="demo-access-key"
        type="password"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        value={key}
        onChange={(e) => setKey(e.target.value)}
        className="h-11 w-full"
        data-testid="demo-access-key-input"
      />
      <Button
        type="submit"
        size="lg"
        className="self-start"
        disabled={sending || !key.trim()}
        data-testid="demo-access-submit"
      >
        {sending ? "Checking…" : "Open the demo"}
      </Button>
      {errorMessage ? (
        <p className="text-sm text-fg-muted" role="alert" data-testid="demo-access-key-error">
          {errorMessage}
        </p>
      ) : null}
    </form>
  );
}
